/*
 * Browser-side cart guard for LTL checkout.
 *
 * A line property is only presentation data and can be manipulated. The
 * CarrierService registry and Shopify delivery profile remain the authority
 * for whether a checkout can receive a freight rate. This script prevents
 * the common client-side bypasses before the customer reaches checkout.
 */
(() => {
  const LTL_MODE_PROPERTY = '_alamo_freight_mode';
  const LTL_MODE_VALUE = 'ltl_live';
  const LTL_MAX_QUANTITY_PROPERTY = '_alamo_freight_max_quantity';
  let cartAssessment = null;
  let cartCheckInFlight = false;

  const cartUrl = () => window.routes?.cart_url || '/cart';
  const checkoutTriggers = '[name="checkout"], #checkout, .shopify-payment-button__button';

  const isLtlLine = (item) => item?.properties?.[LTL_MODE_PROPERTY] === LTL_MODE_VALUE;

  function quantityCap(item) {
    const parsed = Number.parseInt(item?.properties?.[LTL_MAX_QUANTITY_PROPERTY] || '1', 10);
    // The current live profiles use one or three. Invalid/tampered values fail closed.
    return Number.isInteger(parsed) && (parsed === 1 || parsed === 3) ? parsed : 1;
  }

  function assessCart(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const ltlItems = items.filter(isLtlLine);
    if (!ltlItems.length) return null;

    const issues = [];
    if (ltlItems.length > 1) {
      issues.push('Freight checkout supports one LTL cart line. Remove the second LTL product or combine the order through a manual freight quote.');
    }
    if (items.length !== ltlItems.length) {
      issues.push('Freight checkout cannot be combined with parcel parts or other products. Place the other item in a separate order.');
    }
    ltlItems.forEach((item) => {
      const cap = quantityCap(item);
      if (Number(item.quantity || 0) > cap) {
        const noun = cap === 1 ? 'one unit' : `${cap} units`;
        issues.push(`This LTL item is limited to ${noun} in one checkout.`);
      }
    });

    return issues.length ? { cart, ltlItems, issues } : null;
  }

  function makeNotice(location, assessment) {
    const notice = document.createElement('aside');
    notice.className = 'alamo-ltl-cart-notice';
    notice.dataset.alamoLtlCartNotice = location;
    notice.setAttribute('role', 'alert');
    const list = assessment.issues.map((issue) => `<li>${issue}</li>`).join('');
    notice.innerHTML = `
      <p><strong>Update this freight cart before checkout.</strong></p>
      <ul>${list}</ul>
      <div><a class="button" href="${cartUrl()}">Review cart</a></div>`;
    return notice;
  }

  function renderCartGuard() {
    document.querySelectorAll('[data-alamo-ltl-cart-notice]').forEach((node) => node.remove());
    document.body.classList.toggle('alamo-ltl-cart-invalid', Boolean(cartAssessment));
    if (!cartAssessment) return;

    const cartPage = document.querySelector('#main-cart-items .js-contents');
    if (cartPage) cartPage.prepend(makeNotice('cart-page', cartAssessment));
    const miniCart = document.querySelector('mini-cart .drawer-content');
    if (miniCart) miniCart.prepend(makeNotice('mini-cart', cartAssessment));
  }

  function focusCartNotice() {
    const notice = document.querySelector('[data-alamo-ltl-cart-notice="cart-page"]')
      || document.querySelector('[data-alamo-ltl-cart-notice]');
    if (!notice) return;
    notice.scrollIntoView({ behavior: 'smooth', block: 'center' });
    notice.querySelector('a')?.focus({ preventScroll: true });
  }

  async function refreshCartGuard() {
    if (cartCheckInFlight) return;
    cartCheckInFlight = true;
    try {
      const response = await fetch(`${cartUrl()}.js`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return;
      cartAssessment = assessCart(await response.json());
      renderCartGuard();
    } catch (error) {
      console.warn('LTL cart guard could not inspect the cart.', error);
    } finally {
      cartCheckInFlight = false;
    }
  }

  document.addEventListener('click', (event) => {
    if (!cartAssessment || !event.target.closest?.(checkoutTriggers)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusCartNotice();
  }, true);

  document.addEventListener('submit', (event) => {
    if (!cartAssessment) return;
    const isCheckoutSubmission = event.submitter?.name === 'checkout'
      || event.target?.action?.includes('/checkout');
    if (!isCheckoutSubmission) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    focusCartNotice();
  }, true);

  document.addEventListener('cart:item-add', refreshCartGuard);
  document.addEventListener('cart:item-quantity-update', refreshCartGuard);
  document.addEventListener('cart:item-remove', refreshCartGuard);
  document.addEventListener('mini-cart:opened', refreshCartGuard);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartGuard);
    subscribe(PUB_SUB_EVENTS.cartItemAdd, refreshCartGuard);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refreshCartGuard);
  else refreshCartGuard();
})();
